// Core type definitions for the Fanbasis Payment SDK

export interface PrefillAddress {
  country?: string;
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
}

export interface PrefillConfig {
  email?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  address?: PrefillAddress;
}

export interface FieldControl {
  hide?: boolean;
  disable?: boolean;
}

export interface FieldsConfig {
  email?: FieldControl;
  first_name?: FieldControl;
  last_name?: FieldControl;
  phone?: FieldControl;
  address?: FieldControl;
}

export type PrefillableFieldName = keyof FieldsConfig;

export interface CustomizationParams {
  theme: 'light' | 'dark';
  show_product_info: boolean;
  product_layout: 'left' | 'above';
  show_coupon_row: boolean;
  coupon_row_disclaimer?: string;
  accent_color: string;
  background_color?: string;
  label_color?: string;
  input_background_color?: string;
  product_text_color?: string;
  heading_color?: string;
  secondary_color?: string;
  border_color?: string;
  surface_color?: string;
  product_image?: string;
  /** @deprecated Use `fields` for per-field visibility control instead */
  billing_display_fields?: string;
  billing_form_placement?: 'above' | 'left';
  show_headings?: boolean;
  show_powered_by?: boolean;
  prefill?: PrefillConfig;
  fields?: FieldsConfig;
}

export type Theme = 'light' | 'dark';
export type ProductLayout = 'left' | 'above';
export type BillingFormPlacement = 'above' | 'left';

export interface RedirectSettings {
  success_redirect_url?: string;
  failure_redirect_url?: string;
  always_redirect?: boolean;
}

export interface CheckoutConfig {
  // Core configuration - only the required parameters
  creatorId: string;
  productId: string;
  bumpProductIds?: string[];
  couponCode?: string;
  affiliateCode?: string;
  checkoutSessionSecret: string;
  environment: 'sandbox' | 'production';
  overrideBaseUrl?: string;
  showAllAddons?: boolean;

  /** When true, shows a phone number field with country code selector on the checkout form */
  collectPhone?: boolean;

  metadata?: Record<string, string>;

  // Theme customization options
  theme?: CustomizationParams;
  containerOptions?: {
    width?: string;
    height?: string;
  };

  // Redirect settings
  redirectSettings?: RedirectSettings;

  // Programmatic form submission
  showSubmitButton?: boolean;
}

// Simplified state - only what's needed for the core functionality
export interface CheckoutState {
  isOpen: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  error: PaymentError | null;
}

export enum PaymentErrorCode {
  INVALID_CONFIG = 'INVALID_CONFIG',
  CREATOR_ID_REQUIRED = 'CREATOR_ID_REQUIRED',
  PRODUCT_ID_REQUIRED = 'PRODUCT_ID_REQUIRED',
  CHECKOUT_SESSION_SECRET_REQUIRED = 'CHECKOUT_SESSION_SECRET_REQUIRED',
  FIELD_VALIDATION_ERROR = 'FIELD_VALIDATION_ERROR',
  FIELD_NOT_SETTABLE = 'FIELD_NOT_SETTABLE',
  IFRAME_NOT_READY = 'IFRAME_NOT_READY',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

export interface FieldValueData {
  field: string;
  value: string | PrefillAddress | null;
}

export class PaymentError extends Error {
  public readonly code: PaymentErrorCode;
  public readonly details?: any;

  constructor(code: PaymentErrorCode, message: string, details?: any) {
    super(message);
    this.name = 'PaymentError';
    this.code = code;
    this.details = details;
  }
}

export interface CheckoutSuccessData {
  transactionId: string;
  amount: number;
  currency: string;
  customer: any;
  metadata: any;
}

// Programmatic form submission interfaces
export interface SubmitFormOptions {
  paymentMethod?: string;
  forceValidation?: boolean;
}

export interface FormSubmittingData {
  paymentMethod: string;
  timestamp: number;
  timestamp_iso?: string;
}

export interface FormSubmissionErrorData {
  type: 'FORM_SUBMISSION_ERROR';
  timestamp: number;
  data: {
    errorCode: string;
    errorMessage: string;
    retryable: boolean;
  };
}

export interface FormReadyData {
  timestamp: number;
}

export interface Addon {
  id: string;
  title: string;
  price: number;
  description: string;
  subscription_details?: {
    starting_on: string;
    payment_frequency: string;
    free_trial_days: number;
    recurring_subtotal: number;
  };
}

export interface AddonsChangedData {
  selectedAddons: string[];
  addons: Addon[];
}

export interface CouponAppliedData {
  code: string;
  discountAmount: number;
  newTotal: number;
}

export interface CouponErrorData {
  code: string;
  error: string;
}

export interface FormValidationFieldStatus {
  valid: boolean;
  error: string | null;
}

export interface FormValidationData {
  isValid: boolean;
  fields: Record<string, FormValidationFieldStatus>;
}

// Simplified events - only core checkout events
export interface CheckoutEvents {
  'checkout:opened': () => void;
  'checkout:closed': () => void;
  'checkout:loaded': () => void;
  'checkout:error': (error: PaymentError) => void;
  'checkout:success': (data: CheckoutSuccessData) => void;
  'form:ready': (data: FormReadyData) => void;
  'form:submitting': (data: FormSubmittingData) => void;
  'form:submission_error': (data: FormSubmissionErrorData) => void;
  'form:validation': (data: FormValidationData) => void;
  // Addon events
  'addons:changed': (data: AddonsChangedData) => void;
  // Coupon events
  'coupon:applied': (data: CouponAppliedData) => void;
  'coupon:error': (data: CouponErrorData) => void;
  'field:value': (data: FieldValueData) => void;
}

export type CheckoutEventName = keyof CheckoutEvents;
export type CheckoutEventListener<T extends CheckoutEventName> =
  CheckoutEvents[T];
